import { useMutation, useQuery } from "@apollo/client";
import {
  ProductAlternativesCard,
  ProductDeploymentCard,
  ProductDetailsCard,
  ProductGallery,
  ProductGovernments,
  ProductHeader,
  ProductIntegrationsCard,
  ProductNewsworthy,
  ProductSharing,
  ProductSiblingsCard,
  ProductSolutionsCard,
  ProductVendorCard,
} from "@govlaunch/product";
import { IProductFieldsFragment } from "@govlaunch/product/src/fragments/__generated__/ProductFieldsFragment.generated";
import { StoryRelatedStories } from "@govlaunch/story";
import {
  Anchor,
  AnchorButton,
  Box,
  Button,
  DownloadResource,
  Flex,
  Grid,
  Heading,
  Highlights,
  LinkResource,
  LoadingSpinner,
  PageContainer,
  PageGridContainer,
  ResourcesBox,
  Text,
} from "@govlaunch/web";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelfie } from "~/components/auth/Auth";
import userHasSearchAccess from "~/components/makers/userHasSearchAccess";
import ProductPageSeo from "~/components/seo/ProductPageSeo";
import UpgradeToMakersProBanner from "~/components/vendor/profile/UpgradeToMakersProBanner";
import { findLanguageByLocale } from "~/intl/locales";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import useIsMobile from "~/lib/hooks/useIsMobile";
import ToggleBookmarkMutation from "~/lib/mutations/ToggleBookmarkMutation";
import {
  IToggleBookmarkMutation,
  IToggleBookmarkMutationVariables,
} from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import Redirect from "~/lib/route/Redirect";
import { useIntlLocale } from "~/lib/withIntl";
import { IBookmarkType } from "~/types/types";
import nextLinkHref from "~/src/utils/nextLinkHref";
import stringOrFirst from "~/src/utils/stringOrFirst";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
import TranslateTo from "~/src/components/translation/TranslateTo";
import canSeeGovernmentsUsing from "~/src/components/product/acl/canSeeGovernmentsUsing";
import getProductViewMode, { PreviewMode } from "~/src/components/product/getProductViewMode";
import IsProductTranslationEnabledQuery from "~/src/components/product/graphql/IsProductTranslationEnabledQuery";
import ProductBySlugQuery from "~/src/components/product/graphql/ProductBySlugQuery";
import ToggleProductStackMutation from "~/src/components/product/graphql/ToggleProductStackMutation";
import {
  IIsProductTranslationEnabledQuery,
  IIsProductTranslationEnabledQueryVariables,
} from "~/src/components/product/graphql/__generated__/IsProductTranslationEnabledQuery.generated";
import {
  IProductBySlugQuery,
  IProductBySlugQueryVariables,
} from "~/src/components/product/graphql/__generated__/ProductBySlugQuery.generated";
import {
  IToggleProductStackMutation,
  IToggleProductStackMutationVariables,
} from "~/src/components/product/graphql/__generated__/ToggleProductStackMutation.generated";
import ProductSymbolIcon from "~/src/components/product/icons/ProductSymbolIcon";
import ProductProjects from "~/src/components/product/ProductProjects";
import TopLevelPreviewProductNavbar from "~/src/components/product/TopLevelPreviewProductNavbar";

export default function ProductBySlugPage() {
  const viewer = useSelfie();
  const router = useRouter();
  const isMobile = useIsMobile();
  const intl = useIntl();
  const { locale } = useIntlLocale();

  const productSlug = stringOrFirst(router.query.productSlug);

  const { data: translationCheckData } = useQuery<
    IIsProductTranslationEnabledQuery,
    IIsProductTranslationEnabledQueryVariables
  >(IsProductTranslationEnabledQuery, {
    variables: {
      slug: productSlug,
    },
    fetchPolicy: "network-only",
  });

  const lang = stringOrFirst(router.query.lang);
  const langExists = typeof lang !== "undefined" && lang.length > 0 && lang !== "en-US";
  const targetLanguage = lang || locale;

  const showTranslateButton =
    typeof translationCheckData !== "undefined" &&
    translationCheckData.isTranslationEnabled !== null &&
    translationCheckData.isTranslationEnabled &&
    (langExists || locale !== "en-US");

  const [toggleBookmark] = useMutation<IToggleBookmarkMutation, IToggleBookmarkMutationVariables>(
    ToggleBookmarkMutation,
  );

  const { loading, data, refetch } = useQuery<IProductBySlugQuery, IProductBySlugQueryVariables>(ProductBySlugQuery, {
    variables: {
      slug: productSlug,
      language: findLanguageByLocale(lang),
    },
    fetchPolicy: "network-only",
  });

  const [toggleProductStack] = useMutation<IToggleProductStackMutation, IToggleProductStackMutationVariables>(
    ToggleProductStackMutation,
  );

  if (loading) {
    return (
      <NavbarLayout>
        <PageGridContainer justifyContent="center" py={6}>
          <LoadingSpinner />
        </PageGridContainer>
      </NavbarLayout>
    );
  }

  const product = data?.product;

  if (!product) {
    return <Redirect to="/" />;
  }

  const viewAs = getProductViewMode(
    viewer,
    product,
    router.query.preview as PreviewMode,
    product.company && product.company.viewerIsMember,
  );
  const searchAccess = userHasSearchAccess(viewer) || (product.company && product.company.viewerIsMember);
  const isPreviewing = ["makers", "basic"].includes(stringOrFirst(router.query.preview));

  if (viewer && viewer.__typename === "VendorUser" && !searchAccess) {
    return (
      <NavbarLayout>
        <PageContainer>
          <UpgradeToMakersProBanner />
        </PageContainer>
      </NavbarLayout>
    );
  }

  const renderProductLink = (product: NonNullable<Pick<IProductFieldsFragment, "slug">>, children: ReactNode) => {
    return (
      <Link key={product.slug} href={`/products/${product.slug}`} scroll={false}>
        {children}
      </Link>
    );
  };

  const productSharing = (
    <ProductSharing
      product={product}
      onToggleBookmark={() => {
        toggleBookmark({
          refetchQueries: refetchQueries(["Government"]),
          variables: {
            targetType: IBookmarkType.Product,
            targetId: product._id,
          },
        });
      }}
    />
  );

  return (
    <NavbarLayout>
      <ProductPageSeo product={product} />

      {product.company && product.company.viewerIsMember && isPreviewing && (
        <PageContainer>
          <TopLevelPreviewProductNavbar
            productSlug={product.slug}
            preview={viewAs}
            isEnrichedProfile={product.company.plan.name !== "Basic"}
          />
        </PageContainer>
      )}
      <PageGridContainer
        gridTemplateColumns="max-content max-content"
        alignItems="center"
        justifyContent="space-between"
        gridColumnGap={6}
        mt={6}
      >
        {isMobile && false && (
          <Heading color="gray.0" textTransform="uppercase" fontSize="lg">
            <FormattedMessage defaultMessage="Products" id="7NFfmz" />
          </Heading>
        )}

        <Grid gridTemplateColumns="40px max-content" alignItems="center" gridColumnGap={2}>
          <ProductSymbolIcon />

          <div>
            <Link
              href={product.company ? "/vendors/[vendorSlug]" : "/products"}
              as={product.company ? `/vendors/${product.company.slug}` : `/products`}
            >
              <Anchor color="grey.600" textTransform="uppercase">
                {product.company && !isMobile ? (
                  <FormattedMessage
                    defaultMessage="All {companyName} Products"
                    id="gyhajX"
                    values={{ companyName: product.company.name }}
                  />
                ) : (
                  <FormattedMessage defaultMessage="All Products" id="enAu2b" />
                )}
              </Anchor>
            </Link>
          </div>
        </Grid>
      </PageGridContainer>

      {isMobile && (
        <Box my={4}>
          <PageGridContainer gridTemplateColumns={["1fr", "1fr", "3fr 1fr"]}>
            <TranslateTo
              langExists={langExists}
              showTranslateButton={showTranslateButton}
              refetch={() => {
                if (refetch) {
                  refetch({
                    slug: productSlug,
                    language: findLanguageByLocale(targetLanguage),
                  });
                }
              }}
              refetchOriginal={() => {
                if (refetch) {
                  refetch({
                    slug: productSlug,
                    language: null,
                  });
                }
              }}
            />
          </PageGridContainer>
        </Box>
      )}

      <Box my={4} pb={6}>
        <PageGridContainer gridTemplateColumns={["1fr", "1fr", "3fr 1fr"]}>
          <Box>
            <ProductHeader
              product={product}
              translationButton={() => {
                if (isMobile) {
                  return null;
                }

                return (
                  <TranslateTo
                    langExists={langExists}
                    showTranslateButton={showTranslateButton}
                    refetch={() => {
                      if (refetch) {
                        refetch({
                          slug: productSlug,
                          language: findLanguageByLocale(targetLanguage),
                        });
                      }
                    }}
                    refetchOriginal={() => {
                      if (refetch) {
                        refetch({
                          slug: productSlug,
                          language: null,
                        });
                      }
                    }}
                  />
                );
              }}
              isMobile={isMobile}
              renderTagLinkWrap={(tag, children) => {
                const linkHref = nextLinkHref("/collections/[collectionSlug]/[[...tab]]", {
                  collectionSlug: tag.slug,
                });

                const linkAs = `/collections/${tag.slug}/products`;

                return (
                  <Link key={linkAs} href={linkHref} as={linkAs}>
                    <a href={linkAs}>{children}</a>
                  </Link>
                );
              }}
            />

            <Box bg="white" borderRadius="md" border="1px" borderColor="gray.100" p={4} mt={5}>
              <ProductGallery product={product} mt={3} />

              {product.about && (
                <>
                  <Box height="1px" bg="gray.100" mt={8} mb={8} />

                  <Text fontSize="sm">{product.about}</Text>
                </>
              )}

              {!isMobile && (
                <Flex mt={6}>
                  <Box marginLeft="auto">{productSharing}</Box>
                </Flex>
              )}
            </Box>

            {viewAs === "makers" && product.highlights.length > 0 && (
              <Highlights highlights={product.highlights} mt={6} />
            )}

            {viewAs === "makers" && <ProductNewsworthy stacked={isMobile} product={product} mt={5} />}

            {viewAs === "makers" && <ProductProjects product={product} mt={5} />}

            {canSeeGovernmentsUsing(product, viewer) && (
              <ProductGovernments
                product={product}
                mt={5}
                renderLinkWrap={(government, children) => {
                  const linkHref = nextLinkHref("/government", {
                    governmentSlug: government.slug,
                  });

                  const linkAs = `/governments/${government.slug}`;

                  return (
                    <Link key={government.slug} as={linkAs} href={linkHref}>
                      <a href={linkAs}>{children}</a>
                    </Link>
                  );
                }}
              />
            )}
          </Box>

          {isMobile && (
            <Box maxWidth={300} mx="auto">
              {productSharing}
            </Box>
          )}

          <Box mt={3}>
            {viewer?.__typename === "GovernmentUser" && (
              <Button
                mb={2}
                variantColor={product.inViewerStack ? "red" : "teal"}
                variant="solid"
                fullWidth={true}
                leftIcon={(props) => (
                  <svg width={25} height={25} viewBox="0 0 25 25" fill="none" {...props}>
                    <mask id="salkj4lkz" fill="#fff">
                      <rect x={0.648} y={6.527} width={24} height={17.143} rx={1} />
                    </mask>
                    <rect
                      x={0.648}
                      y={6.527}
                      width={24}
                      height={17.143}
                      rx={1}
                      stroke="#fff"
                      strokeWidth={3.4}
                      mask="url(#salkj4lkz)"
                    />
                    <path stroke="#fff" strokeLinecap="round" d="M4.576 4.313h16.143M8.005.885h9.285" />
                  </svg>
                )}
                onClick={() => {
                  if (viewer && viewer.__typename === "GovernmentUser") {
                    toggleProductStack({
                      variables: {
                        productId: product._id,
                        governmentId: viewer.government._id,
                      },
                    });
                  }
                }}
              >
                {product.inViewerStack ? (
                  <FormattedMessage defaultMessage="Remove from Stack" id="fMgmCu" />
                ) : (
                  <FormattedMessage defaultMessage="Add to Stack" id="7rcJw5" />
                )}
              </Button>
            )}

            {!isMobile && (
              <>
                {product.link && (
                  <AnchorButton
                    href={product.link}
                    external="outside-govlaunch"
                    my={2}
                    variant="outline"
                    variantColor="teal"
                    fullWidth={true}
                  >
                    <FormattedMessage defaultMessage="Visit website" id="YXprDM" />
                  </AnchorButton>
                )}

                {viewAs === "makers" && product.contactPage && (
                  <AnchorButton
                    href={product.contactPage}
                    external="outside-govlaunch"
                    my={2}
                    variant="outline"
                    variantColor="teal"
                    fullWidth={true}
                  >
                    <FormattedMessage defaultMessage="Contact" id="zFegDD" />
                  </AnchorButton>
                )}
              </>
            )}

            <ProductVendorCard
              product={product}
              mt={4}
              renderLinkWrap={(company, children) => {
                const linkHref = nextLinkHref("/vendor", {
                  vendorSlug: company.slug,
                });

                const linkAs = `/vendors/${company.slug}`;

                return (
                  <Link href={linkHref} as={linkAs}>
                    <a href={linkAs}>{children}</a>
                  </Link>
                );
              }}
            />

            {isMobile && (
              <>
                {product.link && (
                  <AnchorButton
                    href={product.link}
                    external="outside-govlaunch"
                    my={2}
                    variant="outline"
                    variantColor="teal"
                    fullWidth={true}
                  >
                    <FormattedMessage defaultMessage="Visit website" id="YXprDM" />
                  </AnchorButton>
                )}

                {viewAs === "makers" && product.contactPage && (
                  <AnchorButton
                    href={product.contactPage}
                    external="outside-govlaunch"
                    my={2}
                    variant="outline"
                    variantColor="teal"
                    fullWidth={true}
                  >
                    <FormattedMessage defaultMessage="Contact" id="zFegDD" />
                  </AnchorButton>
                )}
              </>
            )}

            {viewAs === "makers" && <ProductSiblingsCard product={product} renderLinkWrap={renderProductLink} />}

            {viewAs === "makers" && <ProductDetailsCard product={product} />}

            {viewAs === "makers" && <ProductIntegrationsCard product={product} renderLinkWrap={renderProductLink} />}

            {viewAs === "makers" && <ProductSolutionsCard product={product} />}

            {viewAs === "makers" && <ProductDeploymentCard product={product} />}

            {viewAs === "makers" && product.resources && product.resources.length > 0 && (
              <Box mt={4}>
                <ResourcesBox>
                  {product.resources
                    .map((resource) => {
                      if (resource.__typename === "LinkResource" && resource.url) {
                        return (
                          <LinkResource key={resource.url} href={resource.url}>
                            {resource.title}
                          </LinkResource>
                        );
                      }

                      if (resource.__typename === "DownloadResource" && resource.url) {
                        return (
                          <DownloadResource key={resource.url} href={resource.url}>
                            {resource.title}
                          </DownloadResource>
                        );
                      }

                      return null;
                    })
                    .filter(Boolean)}
                </ResourcesBox>
              </Box>
            )}

            {viewAs === "basic" && <ProductAlternativesCard product={product} renderLinkWrap={renderProductLink} />}

            {viewAs === "makers" && product.stories !== null && product.stories.length > 0 && (
              <StoryRelatedStories
                title={intl.formatMessage(
                  {
                    defaultMessage: "Stories ({numberOfStories})",
                    id: "llfnTe",
                  },
                  {
                    numberOfStories: product.stories.length,
                  },
                )}
                relatedStories={product.stories || []}
                placeholderImageSrc="/static/story/story-card-placeholder.png"
                mt={4}
                renderLinkWrap={(story, children) => {
                  const linkHref = nextLinkHref("/stories/[storySlug]", {
                    storySlug: story.slug,
                  });

                  return (
                    <Link key={story.slug} href={linkHref} as={`/stories/${story.slug}`} scroll={false}>
                      {children}
                    </Link>
                  );
                }}
              />
            )}
          </Box>
        </PageGridContainer>
      </Box>
    </NavbarLayout>
  );
}
